/**
 * FX Exposure card: shared between the single-entity/Personal dashboard's
 * two call sites (previously identical duplicated JSX). Renders a currency
 * breakdown bar plus per-currency native amounts and share percentages from
 * GET /dashboard/fx-exposure (already scope-aware server-side via entity_id
 * or scope -- no change needed here to work for an aggregate).
 *
 * Deliberately NOT shared with GroupOverviewTab.jsx's FxExposurePanel: that
 * one reads a differently-shaped, nested `fxExposure.group` response and
 * renders converted absolute amounts alongside native ones -- a genuine
 * data-shape difference, not just duplicated markup, so unifying it is a
 * separate, bigger refactor out of scope here.
 */
import { Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const FX_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];

export function FxExposureCard({ fxExposure, currency, formatCurrency }) {
  if (!fxExposure || !fxExposure.currencies || fxExposure.currencies.length <= 1) return null;

  const shareKey = `share_pct_${currency.toLowerCase()}`;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          FX Exposure
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex h-5 rounded-md overflow-hidden mb-3">
          {fxExposure.currencies.map((c, i) => {
            const pct = c[shareKey] ?? 0;
            if (pct < 0.5) return null;
            return (
              <div
                key={c.currency}
                style={{ width: `${pct}%`, backgroundColor: FX_COLORS[i % FX_COLORS.length] }}
                className="flex items-center justify-center text-[9px] text-white font-medium"
              >
                {pct >= 10 ? `${c.currency} ${pct}%` : ''}
              </div>
            );
          })}
        </div>
        <div className="space-y-1.5">
          {fxExposure.currencies.map((c, i) => (
            <div key={c.currency} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: FX_COLORS[i % FX_COLORS.length] }} />
                <span className="font-medium text-xs">{c.currency}</span>
              </div>
              <div className="flex items-center gap-3 font-mono text-xs">
                <span className="text-muted-foreground">
                  {formatCurrency(parseFloat(c.native), c.currency)}
                </span>
                <span>{c[shareKey]}%</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
